import React, { memo, forwardRef, useState, useCallback, useEffect, useRef } from 'react';
import { endOfMonth } from '@internationalized/date';
import { mergeProps, filterDOMProps } from '@react-aria/utils';
import { useDateFormatter } from '@react-aria/i18n';
import { usePress } from '@react-aria/interactions';
import { LeftIcon, RightIcon } from '@gwc/icons';
import { Button } from '../button';
import { CalendarGrid } from './CalendarGrid';
import { CalendarContext } from './contexts';
import { Box } from '../box';
import { Typography } from '../typography';
import { Leads } from '../slots';
import { YearsSelect } from './YearsSelect';
import { MonthsSelect } from './MonthsSelect';
import styles from './styles.module.css';
export const CalendarBase = memo(forwardRef(function CalendarBase(props, ref) {
    const { state, size = 'm', locale, value, defaultValue, onChange, prevButtonProps, nextButtonProps, calendarProps, singleMonth, minValueReason, maxValueReason, minDaysReason, reasonPlacement, ...rest } = props;
    const isRange = !!('highlightedRange' in state) && !singleMonth;
    const { isDisabled: isPrevDisabled, ...restPrevButtonProps } = prevButtonProps;
    const { isDisabled: isNextDisabled, ...restNextButtonProps } = nextButtonProps;
    const yearTimerRef = useRef(0);
    const [selectedYear, setSelectedYear] = useState(state.visibleRange.start.year);
    const [yearSelectOpened, setYearSelectOpened] = useState(false);
    const onYearsClose = useCallback(() => {
        // если вызывать сразу, то в кейсе, когда это происходит посреди выбора ренжа,
        // логика внутри useRangeCalendar закроет календарь, а это нам не надо
        yearTimerRef.current = window.requestAnimationFrame(() => {
            setYearSelectOpened(false);
        });
    }, []);
    const onYearPress = useCallback(() => {
        setYearSelectOpened(true);
    }, []);
    const onYearSelected = (year) => {
        // HACK state не дает перключить нормально нужный год, нужно сфокусить подходящую дату
        let nextFocusedDate = state.visibleRange.start
            .copy()
            .set({ year: year, day: state.focusedDate.day });
        // связано с тем как хук арии выравнивает календарь при отображении двух месяцев
        if (isRange && state.focusedDate.compare(nextFocusedDate) >= 0) {
            nextFocusedDate = nextFocusedDate.add({ months: 1 });
        }
        state.setFocusedDate(nextFocusedDate);
        onYearsClose();
    };
    const monthTimerRef = useRef(0);
    const [selectedMonth, setSelectedMonth] = useState(
    // для ренджей нужно число от 1 до 6 (номер пары, например 1 = январь - февраль)
    isRange
        ? Math.ceil(state.visibleRange.start.month / 2)
        : state.visibleRange.start.month);
    const [monthSelectOpened, setMonthSelectOpened] = useState(false);
    const onMonthsClose = useCallback(() => {
        // если вызывать сразу, то в кейсе, когда это происходит посреди выбора ренжа,
        // логика внутри useRangeCalendar закроет календарь, а это нам не надо
        monthTimerRef.current = window.requestAnimationFrame(() => {
            setMonthSelectOpened(false);
        });
    }, []);
    const onMonthPress = useCallback(() => {
        setMonthSelectOpened(true);
    }, []);
    const onMonthSelected = (month) => {
        month = isRange ? month * 2 : month;
        // HACK state не дает перключить нормально нужный год, нужно сфокусить подходящую дату
        let nextFocusedDate = state.visibleRange.start
            .copy()
            .set({ month: month, day: state.focusedDate.day });
        // связано с тем как хук арии выравнивает календарь при отображении двух месяцев
        if (isRange && state.focusedDate.compare(nextFocusedDate) < 0) {
            nextFocusedDate = nextFocusedDate.subtract({ months: 1 });
        }
        state.setFocusedDate(nextFocusedDate);
        onMonthsClose();
    };
    // переключаем выбранный месяц/год при изменении видимого ренджа
    // это покрывает как переключение через списки, так и через кнопки вперед/назад
    useEffect(() => {
        const currentVisibleMonth = isRange
            ? Math.ceil(state.visibleRange.start.month / 2)
            : state.visibleRange.start.month;
        const currentVisibleYear = state.visibleRange.start.year;
        if (selectedMonth !== currentVisibleMonth) {
            setSelectedMonth(currentVisibleMonth);
        }
        if (selectedYear !== currentVisibleYear) {
            setSelectedYear(currentVisibleYear);
        }
    }, [state.visibleRange.start]);
    useEffect(() => () => {
        if (yearTimerRef.current)
            window.cancelAnimationFrame(yearTimerRef.current);
        if (monthTimerRef.current)
            window.cancelAnimationFrame(monthTimerRef.current);
    }, []);
    const startDate = state.visibleRange.start.add({ months: 1 });
    const endDate = endOfMonth(startDate);
    return (React.createElement(CalendarContext.Provider, { value: {
            state,
            size,
            locale,
            minValueReason,
            maxValueReason,
            minDaysReason,
            reasonPlacement,
        } },
        React.createElement(Box, { ...mergeProps(filterDOMProps(rest), calendarProps), className: styles.calendar, ref: ref },
            React.createElement(Box, { className: styles.gridWrap },
                React.createElement(Box, { className: styles.header, pb: "s" },
                    React.createElement(Button, { ...restPrevButtonProps, size: size, variant: "outline", disabled: isPrevDisabled, shape: "circle", type: "button", className: styles.prevBtn },
                        React.createElement(Leads.Icon, null,
                            React.createElement(LeftIcon, null))),
                    React.createElement(PageControl, { ...state.visibleRange, timeZone: state.timeZone, onYearPress: onYearPress, onMonthPress: onMonthPress }),
                    React.createElement(Button, { ...restNextButtonProps, size: size, variant: "outline", disabled: isNextDisabled, shape: "circle", type: "button", className: styles.nextBtn },
                        React.createElement(Leads.Icon, null,
                            React.createElement(RightIcon, null)))),
                React.createElement(Box, { className: styles.calendarWrap },
                    React.createElement(CalendarGrid, null),
                    isRange && React.createElement(CalendarGrid, { startDate: startDate, endDate: endDate }),
                    React.createElement(YearsSelect, { onSelect: onYearSelected, value: selectedYear, onClose: onYearsClose, opened: yearSelectOpened }),
                    React.createElement(MonthsSelect, { onSelect: onMonthSelected, value: selectedMonth, onClose: onMonthsClose, opened: monthSelectOpened, isRange: isRange }))))));
}));
function PageControl({ start, end, timeZone, onYearPress, onMonthPress, }) {
    var _a, _b, _c, _d;
    const dateFormatter = useDateFormatter({
        month: 'long',
        year: 'numeric',
    });
    const dateParts = dateFormatter.formatToParts(start.toDate(timeZone));
    const month = (_a = dateParts.find(x => x.type === 'month')) === null || _a === void 0 ? void 0 : _a.value;
    const year = (_b = dateParts.find(x => x.type === 'year')) === null || _b === void 0 ? void 0 : _b.value;
    const endDateParts = dateFormatter.formatToParts(end.toDate(timeZone));
    const endMonth = (_c = endDateParts.find(x => x.type === 'month')) === null || _c === void 0 ? void 0 : _c.value;
    const endYear = (_d = endDateParts.find(x => x.type === 'year')) === null || _d === void 0 ? void 0 : _d.value;
    const { pressProps: yearPressProps } = usePress({ onPressStart: onYearPress });
    const { pressProps: monthPressProps } = usePress({ onPressStart: onMonthPress });
    return (React.createElement(Box, { flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 },
        React.createElement(Typography.Nav, { ...monthPressProps, variant: "body2", color: "main", Component: "div", tabIndex: 0, className: styles.focusableText, role: "button" }, month === endMonth && year === endYear ? (month) : (React.createElement(React.Fragment, null,
            React.createElement("span", null, month),
            React.createElement("span", null, " \u2013 "),
            React.createElement("span", null, endMonth)))),
        React.createElement(Typography, { ...yearPressProps, variant: "caption1", color: "main", Component: "div", tabIndex: 0, className: styles.focusableText, role: "button" }, year)));
}
